import { useContext, useEffect, useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_NEWS_PREVIEW_INFORMATION_BY_ID } from "src/api/news";
import {
    GetNewsPreviewInformationById,
    GetNewsPreviewInformationByIdVariables
} from "src/api/generated/GetNewsPreviewInformationById";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { getRoleKey } from "src/network/User";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import { Route, selectRoute } from "src/config/routes";
import { runInAction } from "mobx";
import { useParams } from "react-router";
import { NewsPreview as NewsPreviewComponent } from "src/components/news/preview/NewsPreview";
import { NetworkConfig } from "src/network/NetworkConfig";
import { useHistory } from "react-router-dom";
import { MessageType } from "src/components/notifications/Notifier";
import { DELETE_NEWS_BY_ID } from "src/api/news";
import { DeleteNewsById, DeleteNewsByIdVariables } from "src/api/generated/DeleteNewsById";
import { NewsState } from "src/api/news";

import styles from "./NewsPreview.module.css";
import { NewsInfoTile } from "./NewsInfoTile";
import Axios from "src/network/Axios";
import { getHtmlStringForPreview } from "src/utils/HTMLUtils";
import { useNewsInsertedEventSubscription } from "src/hooks/news/useNewsInsertedEventSubscription";

interface Params {
    newsid: string;
    realestateid: string;
}

const NewsPreviewBase = () => {
    const { realestateid, newsid } = useParams<Params>();
    const { t } = useTranslation();
    const { navStore, authStore, uiStore } = useContext(RootStoreContext);
    const history = useHistory();

    useEffect(() => {
        runInAction(() => {
            requestAnimationFrame(() => {
                navStore.setTitle(t("screens.realestate.news_preview.title"));
                navStore.setBackbutton(
                    selectRoute(Route.dashboard, authStore.user?.role, { realestateid: realestateid }),
                    t("screens.realestate.dashboard.title")
                );
            });
        });
    }, [navStore, t, authStore.user, realestateid, newsid]);

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: {
                    type: msgType,
                    autoClose: msgType === MessageType.ERROR ? false : 3000
                }
            });
        },
        [uiStore]
    );

    const { data, loading, error, refetch } = useQuery<
        GetNewsPreviewInformationById,
        GetNewsPreviewInformationByIdVariables
    >(GET_NEWS_PREVIEW_INFORMATION_BY_ID, {
        variables: {
            newsid: newsid,
            realestateid: realestateid
        }
    });

    useEffect(() => {
        refetch();
    }, [refetch]);

    const [deleteNewsByIdMutation] = useMutation<DeleteNewsById, DeleteNewsByIdVariables>(DELETE_NEWS_BY_ID, {
        onCompleted: () => {
            printStatusMessage(t("screens.realestate.news_preview.delete_success"), MessageType.INFO);
        }
    });

    const hasActiveCoowners =
        data?.active_coowners_count.aggregate?.count !== undefined && data.active_coowners_count.aggregate?.count > 0;

    const hasActiveTenants =
        data?.active_tenants_count.aggregate?.count !== undefined && data.active_tenants_count.aggregate?.count > 0;

    const onNotifyNews = useCallback(async () => {
        if ((hasActiveCoowners || hasActiveTenants) && authStore.user && authStore.token) {
            try {
                await Axios.getAuthInstance().put(
                    NetworkConfig.insertNotificationUrl,
                    {
                        newsid: newsid
                    },
                    {
                        headers: {
                            "x-hasura-role": getRoleKey(authStore.user?.role)
                        }
                    }
                );

                refetch();
                uiStore.setIsNewsInsertedEventLoaded(false);
                printStatusMessage(
                    navStore.isCondominium
                        ? t("screens.realestate.news_preview.save_notification_success")
                        : t("screens.realestate.news_preview.save_notification_success_tenant"),
                    MessageType.INFO
                );
            } catch (error) {
                printStatusMessage(
                    t("screens.realestate.news_preview.save_notification_error", {
                        errorMessage: error
                    }),
                    MessageType.ERROR
                );
            }
        }
    }, [
        newsid,
        authStore,
        uiStore,
        printStatusMessage,
        t,
        refetch,
        hasActiveCoowners,
        navStore.isCondominium,
        hasActiveTenants
    ]);

    const onDeleteNews = useCallback(async () => {
        await deleteNewsByIdMutation({
            variables: {
                newsid: newsid
            }
        });

        uiStore.setHasNewNewsData(true);

        history.replace(
            selectRoute(navStore.isCondominium ? Route.dashboard : Route.news, authStore.user?.role, {
                realestateid: realestateid
            })
        );
    }, [authStore.user?.role, history, realestateid, deleteNewsByIdMutation, newsid, uiStore, navStore.isCondominium]);

    const eventDate = useNewsInsertedEventSubscription(newsid, "");

    const [isNotificationModalShown, setIsNotificationModalShown] = useState(false);

    const handleNotifyConfirm = useCallback(async () => {
        await onNotifyNews();
        setIsNotificationModalShown(false);
    }, [onNotifyNews]);

    const isAlreadyNotified =
        navStore.isCondominium && hasActiveCoowners && (eventDate !== undefined || eventDate != null);

    if (loading) {
        return <Screens.Loading />;
    }
    if (error) {
        return <Screens.Error message={error.message} networkError={error.networkError} />;
    }
    if (!data) {
        return <Screens.Error message={t("error.nodata")} />;
    }

    const news = data.news.length > 0 ? data.news[0] : undefined;

    const state = news?.state ? Number(news.state) : undefined;

    const imageUrl = news?.fileid ? NetworkConfig.openThumbnailUrl + news?.fileid : "";
    const hasNotificationEvents = news?.newsevents.events?.count && news?.newsevents.events?.count > 0 ? true : false;

    return (
        <UI.Container className="my-4">
            <UI.Row>
                <UI.Col md={8} className={`${styles.InfoPreviewContainer} mb-3 mb-md-0`}>
                    <NewsPreviewComponent
                        title={news?.title ?? ""}
                        lead={news?.lead ?? ""}
                        text={getHtmlStringForPreview(news?.text)}
                        date={news?.timestamp}
                        imageUrl={imageUrl}
                        className={styles.NewsPreviewStyles}
                        newsFiles={news?.newsfiles ?? []}
                    />
                </UI.Col>
                <UI.Col md={4}>
                    <UI.Card title={t("screens.realestate.news_preview.news_info_title")}>
                        <NewsInfoTile news={news} />
                    </UI.Card>
                </UI.Col>
            </UI.Row>
            <UI.Row>
                <UI.Col>
                    <div className={`${styles.NewsButtonContainer} mt-3`}>
                        {state === NewsState.Published ? (
                            <>
                                <UI.Button
                                    label={t("labels.notification")}
                                    onClick={() => setIsNotificationModalShown(true)}
                                    className={`${styles.NewsEventButton} d-inline mx-0 mx-md-2`}
                                    disabled={!uiStore.isNewsInsertedEventLoaded}
                                />
                                <UI.Modal
                                    title={t("labels.notification").toString()}
                                    show={isNotificationModalShown}
                                    onClose={() => setIsNotificationModalShown(false)}
                                    backdrop="static"
                                >
                                    <>
                                        <p>
                                            {navStore.isCondominium
                                                ? hasActiveCoowners
                                                    ? eventDate !== undefined || eventDate != null
                                                        ? t("screens.realestate.news_preview.already_notified")
                                                        : t("screens.realestate.news_preview.create_notification")
                                                    : t(
                                                          "screens.realestate.news_preview.no_coowners_by_create_notifiycation"
                                                      )
                                                : t("screens.realestate.news_preview.create_notification_tenant")}
                                        </p>
                                        <div className="d-flex">
                                            {navStore.isCondominium && !hasActiveCoowners ? undefined : (
                                                <UI.Button
                                                    label={isAlreadyNotified ? t("labels.resend") : t("labels.yes")}
                                                    onClick={handleNotifyConfirm}
                                                    className={
                                                        isAlreadyNotified
                                                            ? styles.NewsNotifyButton
                                                            : styles.NewsYesNotifyButton
                                                    }
                                                />
                                            )}
                                            <UI.Button
                                                label={isAlreadyNotified ? t("labels.cancel") : t("labels.no")}
                                                onClick={() => setIsNotificationModalShown(false)}
                                                variant="outline-primary"
                                                className={styles.NewsDiscardButton}
                                            />
                                        </div>
                                    </>
                                </UI.Modal>
                            </>
                        ) : undefined}
                        <UI.Button
                            className={styles.NewsEditButton}
                            variant="outline-primary"
                            label={t("labels.edit")}
                            to={selectRoute(Route.realestateNewsPreviewEdit, authStore.user?.role, {
                                realestateid: realestateid,
                                newsid: newsid,
                                newspreviewnewsid: newsid
                            })}
                        />
                        {hasNotificationEvents ? (
                            <UI.ConfirmationDialog
                                key="customerDeleteDialog"
                                buttonText={t("labels.delete")}
                                modalTitle={t("labels.delete")}
                                confirmationQuestion={
                                    navStore.isCondominium
                                        ? t("screens.realestate.news_preview.delete_confirmation")
                                        : t("screens.realestate.news_preview.delete_confirmation_tenant")
                                }
                                inProgressText={t("screens.realestate.news_preview.delete_in_progress")}
                                onConfirmation={onDeleteNews}
                                buttonClasses={`${styles.NewsDeleteButton} d-inline mx-0 mx-md-2`}
                                isDangerousOperation
                            />
                        ) : (
                            <UI.Button
                                className={`${styles.NewsDeleteButton} d-inline mx-0 mx-md-2`}
                                variant="danger"
                                label={t("labels.delete")}
                                onClick={onDeleteNews}
                            />
                        )}
                    </div>
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};

export const NewsPreview = Sentry.withProfiler(observer(NewsPreviewBase));
